import React, { useEffect, useRef, useState } from 'react';
import { Spinner } from 'reactstrap';
import { NavLink } from 'react-router-dom';
import KaAlert from '../../Common/KaAlert';
import useDisplayMessage from '../../Common/useDisplayMessage';
import SpinnerButton from '../../Common/SpinnerButton';
import { KaSelect, KaSelectOption } from '../../Common/KaSelect';
import useGetSitesNonAdmin from '../../Requests/useGetSitesNonAdmin';
import useFetchTransportsFromTmx from '../../../requests/useFetchTransportsFromTmx';
import useGetTransportSyncStatus from '../../../requests/useGetTransportSyncStatus';
import { useUser } from '../../UserAccess/useUser';

type SyncState = 'idle' | 'fetching' | 'polling' | 'processing' | 'done' | 'error';

const POLL_INTERVAL_MS = 2000;

const TransportSyncPage = () => {
  const user = useUser();
  const displayMessage = useDisplayMessage();
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [syncState, setSyncState] = useState<SyncState>('idle');
  const [notificationId, setNotificationId] = useState('');
  const [statusError, setStatusError] = useState<string | null>(null);

  const getSites = useGetSitesNonAdmin();
  const siteOptions: KaSelectOption[] = (getSites.data ?? [])
    .filter(s => s.isRegistered)
    .map(s => ({ value: s.id, label: s.name }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const selectedOption = siteOptions.find(o => o.value === selectedSiteId) ?? null;

  const fetchTransports = useFetchTransportsFromTmx(selectedSiteId, {
    onSuccess: (data) => {
      setNotificationId(data.notificationId);
      setSyncState('polling');
    },
    onError: (err) => {
      displayMessage.fail(err.message || 'Failed to fetch transports from TMX.');
      setSyncState('error');
    },
  });

  const statusQuery = useGetTransportSyncStatus(selectedSiteId, notificationId, {
    autoQuery: false,
    onSuccess: (data) => {
      if (data.error) {
        setStatusError(data.error);
      } else if (data.complete) {
        setStatusError(null);
        setSyncState('processing');
      } else {
        setStatusError(null);
      }
    },
    onError: () => {
      setStatusError('Failed to retrieve sync status.');
    },
  });

  const statusQueryRef = useRef(statusQuery.query);
  useEffect(() => { statusQueryRef.current = statusQuery.query; });

  useEffect(() => {
    if (syncState !== 'polling') return;
    const interval = setInterval(() => statusQueryRef.current(), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [syncState]);

  const handleSiteChange = (option: KaSelectOption | null) => {
    setSelectedSiteId(option?.value ?? '');
    setSyncState('idle');
    setNotificationId('');
    setStatusError(null);
    displayMessage.clear();
  };

  const handleFetch = () => {
    displayMessage.clear();
    setStatusError(null);
    setSyncState('fetching');
    fetchTransports.request(undefined);
  };

  const handleRetry = () => {
    displayMessage.clear();
    setStatusError(null);
    setSyncState('idle');
  };

  if (!user.isDevelopment) return null;

  return (
    <>
      <div className="ms-n2 pb-3">
        <NavLink className="card-link my-auto text-nowrap" to="/transports" style={{ fontSize: 20 }}>
          <i className="bi-chevron-left" />
          Sync TMX Transports
        </NavLink>
      </div>
      <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />

      {(syncState === 'idle' || syncState === 'fetching') && (
        <div className="d-flex justify-content-center" style={{ paddingTop: '8rem' }}>
          <div style={{ width: '340px' }} className="text-center">
            <div className="mb-4 text-start">
              <label className="form-label fw-semibold mb-1">Site</label>
              <KaSelect
                value={selectedOption}
                options={siteOptions}
                onChange={opt => handleSiteChange(opt as KaSelectOption | null)}
                isDisabled={syncState === 'fetching'}
                placeholder="Select a site..."
              />
            </div>
            <SpinnerButton
              spinning={syncState === 'fetching'}
              className="btn-ghost-primary"
              onClick={handleFetch}
              disabled={!selectedSiteId || syncState === 'fetching'}
            >
              <i className="bi-cloud-arrow-down align-middle me-1" />Fetch Transports from TMX
            </SpinnerButton>
            <p className="text-muted small mt-3">This operation may take a moment to complete.</p>
            <p className="text-muted small mt-1">Constellation will suggest merges for any synced transports that closely resemble existing records.</p>
          </div>
        </div>
      )}

      {syncState === 'polling' && (
        <div className="text-center" style={{ paddingTop: '12rem' }}>
          <Spinner animation="border" variant="primary" className="mb-3" aria-busy="true" style={{ width: '3rem', height: '3rem' }} />
          <p className="text-muted">Waiting for connector at {selectedOption?.label}…</p>
          {statusError && (
            <div className="d-inline-flex align-items-start gap-2 mt-3 px-3 py-2 rounded border text-muted small text-start">
              <i className="bi-octagon-fill traffic-light-red mt-1" />
              <div>
                <div className="fw-semibold">Connector error</div>
                <div>{statusError}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {syncState === 'processing' && (
        <div className="text-center" style={{ paddingTop: '12rem' }}>
          <Spinner animation="border" variant="primary" className="mb-3" aria-busy="true" style={{ width: '3rem', height: '3rem' }} />
          <p className="text-muted mb-1">Data received from {selectedOption?.label}.</p>
          <p className="text-muted">Processing…</p>
        </div>
      )}

      {syncState === 'done' && (
        <div className="text-center py-5">
          <i className="bi-check-circle text-success" style={{ fontSize: '2rem' }} />
          <p className="mt-2 text-muted">Fetch complete. Records will appear here once syncing is enabled.</p>
        </div>
      )}

      {syncState === 'error' && (
        <div className="text-center py-4">
          <SpinnerButton spinning={false} className="btn-ghost-secondary" onClick={handleRetry}>
            Try again
          </SpinnerButton>
        </div>
      )}
    </>
  );
};

export { TransportSyncPage };
